/**
 * Authenticated shell — the persistent top bar wrapping the unlocked routes.
 * User interaction bumps the idle auto-lock timer.
 *
 * The top bar is the synced canonical cross-surface SiteHeader (heivol KB
 * design-system/site-header-react/ — edit the canon first, then re-sync). The
 * earlier Phase-3 "reconcile with the canonical SiteHeader" follow-up is DONE:
 * this shell now renders that shared component and only supplies the app-shell
 * right cluster (lock indicator, Lock now, logout pill) via `rightSlot`.
 */
import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { bumpActivity, useSession } from '../store/session';
import { SiteHeader } from './SiteHeader';
import { Button, LockBadge } from './ui';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'scroll', 'touchstart'] as const;

export function Shell() {
  const lock = useSession((s) => s.lock);
  const logout = useSession((s) => s.logout);
  const usingMock = useSession((s) => s.usingMock);

  // Reset the idle auto-lock on any real interaction, anywhere in the window —
  // not just the few events that bubble to this div (a scrolling/reading user
  // must not be locked mid-use).
  useEffect(() => {
    const onActivity = (): void => bumpActivity();
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }
    return () => {
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, onActivity);
      }
    };
  }, []);

  return (
    <div className="min-h-full">
      <SiteHeader
        rightSlot={
          <>
            <LockBadge unlocked />
            <Button variant="ghost" onClick={lock}>
              Lock now
            </Button>
            {!usingMock && (
              <button type="button" className="nav-login" onClick={logout}>
                Log out
              </button>
            )}
          </>
        }
      />
      {usingMock && (
        <div className="bg-warning/10 px-4 py-1.5 text-center text-xs text-warning">
          Dev preview — in-memory mock backend (no Phase-1 server yet). Data is not persisted.
        </div>
      )}
      <main className="mx-auto max-w-3xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
